import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { NotificationsLevel } from "../../../lib/enums";
import { show } from "../../../notifications/notifications";
import { route } from "../../../shim/jsbridge";
import type { CheckPdfResponse } from "../../samples/@types/pdf";
import { PDF_ACTION_TXT } from "../../samples/components/topheader/consts";
import api from "../api";
import useNetworkThreatIntelURLReport from "./useNetworkThreatIntelURLReport";

const URL_PDF_STATUS = "urlPdfStatus";
const MAKE_URL_PDF = "makeUrlPdf";
const DOWNLOAD_URL_PDF = "downloadUrlPdf";

const CHECK_INTERVAL = 3000;

const { pdf_action: pdfAction } = window?.tcbase?.consts ?? {};

const useUrlSummaryPdf = (url?: string) => {
  const [interval, setInterval] = useState<number | false>(CHECK_INTERVAL);
  const [status, setStatus] = useState("make");
  const [isMaking, setIsMaking] = useState(false);
  const { data: urlReport } = useNetworkThreatIntelURLReport(url ?? "");
  const encodedUrl = urlReport?.base64 ?? "";

  const handleError = () => {
    show({
      level: NotificationsLevel.ERROR,
      message: PDF_ACTION_TXT.message.error
    });
    setStatus("make");
    setInterval(false);
    setIsMaking(false);
  };

  const downloadPdfQuery = useMutation({
    mutationKey: [DOWNLOAD_URL_PDF, encodedUrl],
    mutationFn: () => api.urlPdfStatusOrFile(encodedUrl),
    onSuccess: () => {
      window.open(route("pdf_url_summary_get", { encoded_url: encodedUrl }));
    },
    onError: handleError
  });

  const downloadPdf = () => {
    if (!isMaking) return;
    setIsMaking(false);
    show({
      level: NotificationsLevel.INFO,
      message: PDF_ACTION_TXT.message.downloading
    });
    downloadPdfQuery.mutate();
  };

  const makePdfQuery = useMutation({
    mutationKey: [MAKE_URL_PDF, encodedUrl],
    mutationFn: () => api.makeUrlPdf(encodedUrl),
    onSuccess: () => {
      show({
        level: NotificationsLevel.WARNING,
        message: PDF_ACTION_TXT.message.making
      });
      setIsMaking(true);
      setStatus("wait");
      setInterval(CHECK_INTERVAL);
    },
    onError: handleError
  });

  useQuery({
    queryKey: [URL_PDF_STATUS, encodedUrl],
    queryFn: () =>
      api.urlPdfStatusOrFile(encodedUrl).then(response => {
        const responseStatus =
          (response.data as CheckPdfResponse)?.status ?? pdfAction.DOWNLOAD;
        const nextStatus = Object.keys(pdfAction ?? {})
          .find(key => pdfAction[key] === responseStatus)
          ?.toLowerCase();

        setStatus(nextStatus ?? "make");

        if (responseStatus === pdfAction.DOWNLOAD) {
          downloadPdf();
          setInterval(false);
        } else if (responseStatus === pdfAction.WAIT) {
          setInterval(CHECK_INTERVAL);
        } else if (responseStatus === pdfAction.ERROR) {
          //
          if (isMaking) {
            handleError();
          } else {
            setStatus("error");
            setInterval(false);
          }
        } else {
          setInterval(false);
        }

        return response;
      }),
    enabled: Boolean(encodedUrl),
    refetchInterval: interval
  });

  const handleAction = useCallback(() => {
    if (!encodedUrl) return;
    if (status === "download") {
      downloadPdfQuery.mutate();
      return;
    }
    makePdfQuery.mutate();
  }, [status, encodedUrl, makePdfQuery, downloadPdfQuery]);

  return {
    status,
    handleAction,
    disabled: status === "wait" || makePdfQuery.isPending
  };
};

export default useUrlSummaryPdf;
